import { /* getDpr, */ OverlayAnnotations } from '@readcloud/common';
import React from 'react';
import { getPointOfEvent, preventDefault } from '../utils';

function midPointBtw({
  p1,
  p2,
}: {
  p1: OverlayAnnotations.Point;
  p2: OverlayAnnotations.Point;
}) {
  return {
    x: p1.x + (p2.x - p1.x) / 2,
    y: p1.y + (p2.y - p1.y) / 2,
  };
}

function drawPath({
  path,
  context,
  stroke,
  strokeWidth,
  scale,
}: {
  scale: number;
  context: CanvasRenderingContext2D;
  stroke: string;
  strokeWidth: number;
  path: OverlayAnnotations.Path;
}) {
  context.setTransform(scale, 0, 0, scale, 0, 0);
  var p1 = path.points[0];
  var p2 = path.points[1];
  context.lineWidth = strokeWidth;
  context.strokeStyle = stroke;
  context.beginPath();
  context.moveTo(p1.x, p1.y);

  for (var i = 1, len = path.points.length; i < len; i++) {
    // we pick the point between pi+1 & pi+2 as the
    // end point and p1 as our control point
    var midPoint = midPointBtw({ p1, p2 });
    context.quadraticCurveTo(p1.x, p1.y, midPoint.x, midPoint.y);
    p1 = path.points[i];
    p2 = path.points[i + 1];
  }
  // Draw last line as a straight line while
  // we wait for the next point to be able to calculate
  // the bezier control point
  context.lineTo(p1.x, p1.y);
  context.stroke();
}

export const DrawCanvas = React.memo(function DrawCanvas(props: {
  height: number;
  width: number;
  strokeWidth: number;
  stroke: string;
  paths: OverlayAnnotations.Path[];
  onPathsChange: (paths: OverlayAnnotations.Path[]) => void;
  scale: number;
  viewOnly?: boolean;
}) {
  const latestProps = React.useRef(props);
  const isDrawing = React.useRef(false);
  const boundingRect = React.useRef<ClientRect | DOMRect>();
  const [currentCanvas, setCurrentCanvas] = React.useState<HTMLCanvasElement>();
  const currentPath = React.useRef({
    points: [],
    stroke: '#000000',
    strokeWidth: 10,
  } as OverlayAnnotations.Path);
  latestProps.current = props;

  /*   React.useEffect(() => { */
  /*  const ctx = canvas ? canvas.getContext("2d") : undefined;
    if (canvas && ctx) {
      canvas.style.cursor = "crosshair";
      const scale = getDpr(ctx) * latestProps.current.scale;
      canvas.style.height = `${props.height}px`;
      canvas.style.width = `${props.width}px`;
      canvas.height = props.height * scale;
      canvas.width = props.width * scale;
      ctx.lineJoin = ctx.lineCap = "round";
      let isDrawing = false;
      let currentPath = {
        points: [],
        stroke: "#000000",
        strokeWidth: 10
      } as OverlayAnnotations.Path;

      if (props.viewOnly) {
        return;
      }
     

      canvas.addEventListener("pointerup", onPointerUp);
      canvas.addEventListener("pointermove", onPointerMove);
      canvas.addEventListener("pointerdown", onPointerDown);
      return () => {
        canvas.removeEventListener("pointerup", onPointerUp);
        canvas.removeEventListener("pointermove", onPointerMove);
        canvas.removeEventListener("pointerdown", onPointerDown);
      };
    } else {
      return;
    } */
  /*  }, [canvas]); */

  const instantiateCanvas = React.useCallback((canvas: HTMLCanvasElement) => {
    const ctx = canvas ? canvas.getContext('2d') : undefined;
    if (canvas && ctx) {
      setCurrentCanvas(canvas);
    }
  }, []);

  const onPointerDown = React.useCallback(
    function (
      e:
        | React.TouchEvent<HTMLCanvasElement>
        | React.MouseEvent<HTMLCanvasElement>
    ) {
      const canvas = e.currentTarget;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      isDrawing.current = true;
      const point = getPointOfEvent(e);
      const rect = (boundingRect.current = canvas.getBoundingClientRect());
      /*       const scale = getDpr(ctx) * props.scale;
      ctx.setTransform(scale, 0, 0, scale, 0, 0); */
      currentPath.current.points.push({
        x: (point.x - rect.left) / props.scale,
        y: (point.y - rect.top) / props.scale,
      });
    },
    [props.scale]
  );

  const onPointerMove = React.useCallback(
    function (
      e:
        | React.TouchEvent<HTMLCanvasElement>
        | React.MouseEvent<HTMLCanvasElement>
    ) {
      const canvas = e.currentTarget;
      const ctx = canvas.getContext('2d');
      if (!isDrawing.current || !ctx || !boundingRect.current) return;
      const point = getPointOfEvent(e);
      currentPath.current.points.push({
        x: (point.x - boundingRect.current.left) / props.scale,
        y: (point.y - boundingRect.current.top) / props.scale,
      });

      ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);

      for (const path of latestProps.current.paths) {
        drawPath({
          scale: props.scale,
          path,
          context: ctx,
          stroke: props.stroke,
          strokeWidth: props.strokeWidth,
        });
      }
      drawPath({
        scale: props.scale,
        path: currentPath.current,
        context: ctx,
        stroke: props.stroke,
        strokeWidth: props.strokeWidth,
      });
    },
    [props.stroke, props.strokeWidth, props.scale]
  );

  const onPointerUp = React.useCallback(() => {
    isDrawing.current = false;
    if (currentPath.current.points.length > 1) {
      const nextPaths = [...latestProps.current.paths, currentPath.current];
      props.onPathsChange(nextPaths);
    }

    currentPath.current = {
      points: [],
    };
  }, []);

  React.useEffect(() => {
    const ctx = currentCanvas ? currentCanvas.getContext('2d') : undefined;
    if (currentCanvas && ctx) {
      currentCanvas.style.height = `${props.height}px`;
      currentCanvas.style.width = `${props.width}px`;
      currentCanvas.height = props.height;
      currentCanvas.width = props.width;
      ctx.lineJoin = ctx.lineCap = 'round';
      ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);

      for (const path of latestProps.current.paths) {
        drawPath({
          scale: props.scale,
          path,
          context: ctx,
          stroke: props.stroke,
          strokeWidth: props.strokeWidth,
        });
      }
    }
  }, [
    props.scale,
    props.paths,
    currentCanvas,
    props.stroke,
    props.strokeWidth,
    props.height,
    props.width,
  ]);
  const canvasStyle = React.useMemo(
    () =>
      ({
        touchAction: props.viewOnly ? undefined : 'none',
        cursor: props.viewOnly ? 'auto' : 'crosshair',
      } as React.CSSProperties),
    [props.viewOnly]
  );

  React.useEffect(() => {
    if (currentCanvas && !props.viewOnly) {
      currentCanvas.addEventListener('touchstart', preventDefault, {
        passive: false,
      });
      currentCanvas.addEventListener('touchmove', preventDefault, {
        passive: false,
      });
      return () => {
        currentCanvas.removeEventListener('touchstart', preventDefault);
        currentCanvas.removeEventListener('touchmove', preventDefault);
      };
    }
    return;
  }, [currentCanvas, props.viewOnly]);
  return (
    <canvas
      style={canvasStyle}
      onTouchStart={!props.viewOnly ? onPointerDown : undefined}
      onTouchMove={!props.viewOnly ? onPointerMove : undefined}
      onTouchEnd={!props.viewOnly ? onPointerUp : undefined}
      onMouseDown={!props.viewOnly ? onPointerDown : undefined}
      onMouseMove={!props.viewOnly ? onPointerMove : undefined}
      onMouseUp={!props.viewOnly ? onPointerUp : undefined}
      ref={instantiateCanvas}
    />
  );
});
